import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { AuthShell, ErrorNote } from "./Login";
import { ArrowRight, GraduationCap, Home, Handshake, School, Building2, Sparkles } from "lucide-react";

const inputClass =
  "w-full rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-4 py-3.5 text-base font-body text-[#1F2937] outline-none transition-colors focus:border-[#0D9488]";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const partnerMode = params.get("partner") === "1";
  const slug = params.get("slug") || undefined;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(params.get("code") || "");
  // A "reseller context" is set when the visitor arrived via a branded /r/{slug}
  // landing OR was handed a license code. In that mode we hide school / district
  // / reseller signup — license seats are only for teachers & parents.
  const resellerContext = !!slug || !!(code || "").trim();
  const [role, setRole] = useState(partnerMode ? "reseller" : "teacher");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // If the visitor tries to switch to school/district/reseller while still under
  // a reseller context, drop them onto the clean signup page (per product spec).
  useEffect(() => {
    if (partnerMode) return;
    if (resellerContext && ["school_admin", "district_admin", "reseller"].includes(role)) {
      navigate("/signup", { replace: true });
    }
  }, [resellerContext, role, partnerMode, navigate]);

  // Any time the visitor switches into reseller context, snap role back to
  // teacher so the form doesn't submit an invalid combo.
  useEffect(() => {
    if (partnerMode) return;
    if (resellerContext && role !== "teacher" && role !== "parent") setRole("teacher");
  }, [resellerContext, partnerMode]);   // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { name: name.trim(), email: email.trim(), password, role };
      if (role !== "reseller") {
        if (code.trim()) payload.reseller_code = code.trim();
        if (slug) payload.reseller_slug = slug;
      }
      await register(payload);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const ROLE_TILES = resellerContext
    ? [
        { key: "teacher", label: "Teacher", Icon: GraduationCap },
        { key: "parent", label: "Parent", Icon: Home },
      ]
    : [
        { key: "teacher", label: "Teacher", Icon: GraduationCap },
        { key: "parent", label: "Parent", Icon: Home },
        { key: "school_admin", label: "School", Icon: School },
        { key: "district_admin", label: "District", Icon: Building2 },
      ];

  return (
    <AuthShell
      title={partnerMode ? "Become a partner" : "Create your account"}
      subtitle={partnerMode ? "Sign up as a reseller partner to white-label and distribute ZoodLeap." : (resellerContext ? "License codes work for teacher and parent accounts. Sign up below to unlock the full curriculum for your students." : "Sign up as a teacher or parent to add and manage your students.")}
      footer={<>Already have an account? <Link to="/login" className="font-bold text-[#0D9488] hover:underline">Log in</Link></>}
    >
      <form onSubmit={submit}>
        <ErrorNote message={error} />

        {!partnerMode && (
          <>
            <label className="block text-sm font-bold text-[#1F2937] mb-2">I am a…</label>
            <div className={`grid gap-3 mb-4 ${resellerContext ? "grid-cols-2" : "grid-cols-2"}`} data-testid="role-tiles">
              {ROLE_TILES.map((r) => (
                <button key={r.key} type="button" onClick={() => setRole(r.key)} data-testid={`role-${r.key}`}
                  className={`flex items-center justify-center gap-2 rounded-2xl border-2 px-4 py-3 font-bold transition-all ${role === r.key ? "border-[#0D9488] bg-[#0D9488]/5 text-[#0D9488]" : "border-[#E5DECF] text-[#6B7280] hover:bg-[#F2ECE1]"}`}
                  aria-pressed={role === r.key}>
                  <r.Icon size={18} /> {r.label}
                </button>
              ))}
            </div>
            {(role === "school_admin" || role === "district_admin") && !resellerContext && (
              <p className="mb-4 -mt-1 rounded-xl bg-[#7A3FF2]/8 px-3 py-2 font-body text-xs text-[#652BDB]">
                You'll pick a {role === "school_admin" ? "School" : "District"} plan next, then set up your organisation and invite {role === "school_admin" ? "teachers" : "schools"}.
              </p>
            )}
            {resellerContext && (
              <p className="mb-4 -mt-1 flex items-start gap-2 rounded-xl bg-[#0D9488]/10 px-3 py-2 font-body text-xs text-[#0B7F74]" data-testid="reseller-context-note">
                <Sparkles size={14} className="mt-0.5 shrink-0" />
                <span>Signing up through a partner code unlocks the curriculum for your family or classroom. Schools and districts should <Link to="/signup" className="font-bold underline">sign up on the main site</Link> instead.</span>
              </p>
            )}
          </>
        )}

        {partnerMode && (
          <>
            <div className="mb-4 flex items-center gap-2 rounded-2xl border-2 border-[#0D9488] bg-[#0D9488]/5 px-4 py-3 font-bold text-[#0D9488]">
              <Handshake size={18} /> Reseller Partner
            </div>
            <div
              className="mb-5 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4"
              data-testid="reseller-signup-pricing"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">Starts at</div>
                <div className="font-heading text-xl font-extrabold text-[#1F2937]">
                  $1,500<span className="ml-1 text-sm font-semibold text-[#6B7280]">/year</span>
                </div>
              </div>
              <div className="mt-1 font-heading text-lg font-bold text-[#1F2937]">10 seats to launch</div>
              <ul className="mt-3 space-y-1.5 font-body text-sm text-[#1F2937]">
                {[
                  "White-label landing page (yourdomain.com/r/yourbrand)",
                  "Connect your own Stripe — keep 100% of plan revenue",
                  "Scale to Growth (25 seats) or Partner+ (50 seats) any time",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Sparkles size={14} className="mt-0.5 shrink-0 text-[#7A3FF2]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 font-body text-[11px] text-[#6B7280]">
                You'll pick your tier and pay after creating your account. See all reseller tiers on the{" "}
                <Link to="/pricing" className="font-bold text-[#0D9488] hover:underline">pricing page</Link>.
              </p>
            </div>
          </>
        )}

        <label className="block text-sm font-bold text-[#1F2937] mb-2">Full Name</label>
        <input data-testid="signup-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="e.g. Ms. Rivera" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Email</label>
        <input data-testid="signup-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="you@school.com" autoComplete="email" />
        <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Password</label>
        <input data-testid="signup-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="At least 6 characters" autoComplete="new-password" />

        {(role === "teacher" || role === "parent") && !partnerMode && (
          <>
            <label className="block text-sm font-bold text-[#1F2937] mb-2 mt-4">Access / Reseller Code <span className="font-normal text-[#94A3B8]">(optional)</span></label>
            <input data-testid="signup-code-input" value={code} onChange={(e) => setCode(e.target.value)} className={inputClass} placeholder="e.g. ACME-1A2B3C" />
          </>
        )}

        <button type="submit" disabled={busy} data-testid="signup-submit-button" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-lg font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] active:translate-y-0.5 disabled:opacity-50">
          {busy ? "Creating..." : "Create Account"} <ArrowRight size={20} />
        </button>

        {!partnerMode && !resellerContext && (
          <p className="mt-4 text-center font-body text-xs text-[#94A3B8]">
            Are you a reseller? <Link to="/signup?partner=1" className="font-bold text-[#0D9488] hover:underline">Become a partner</Link>
          </p>
        )}
      </form>
    </AuthShell>
  );
}
